// Admin "Policies" — T&C authoring surface (Phase 5A UI polish).
//
// Shopify-Policies-style layout: a single "Written policies" card whose rows are
// the canonical policy types (Reseller Terms / Vendor Terms). A row shows an
// icon + title, a status badge + chevron, and version / EN-TR completeness as
// secondary text. Clicking a row opens a full editor modal (WYSIWYG, EN/TR
// tabs). No technical key field, no same-page editor/applicability form — the
// key is auto-generated; extra policies are added via the card overflow.
//
// English is the controlling version; Türkçe is a translation. Content is
// sanitized server-side (nh3) on save. No real/placeholder text is shipped here.

import { useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ScrollText, FileText, ChevronRight, MoreHorizontal, Plus, Trash2 } from 'lucide-react'
import * as api from '../../../api/agreementsAdmin'
import type { PolicyAgreement, PolicyVersion } from '../../../api/agreementsAdmin'
import { useT } from '../../../i18n'
import { useEscapeKey } from '../../../lib/useEscapeKey'
import { policiesI18n } from '../../../i18n/admin/policies'
import {
  SUPPORTED_AGREEMENT_LOCALES,
  agreementLocaleShortLabel,
} from '../../../i18n/agreement-locales'
import { PolicyEditorModal } from './PolicyEditorModal'
import { DeleteDraftModal } from './DeleteDraftModal'

// Canonical V1 policy types — always shown as rows even before authoring.
//   - reseller/vendor terms: org-level, owner-signed contracts (default).
//   - kvkk_notice: personal (per-user) KVKK Aydınlatma acknowledgment.
//   - commercial_consent: the optional ETK / Ticari İleti opt-in text.
type CanonicalPolicy = { key: string; name: string; perUserAck?: boolean; isMarketing?: boolean }

const CANONICAL_POLICIES: CanonicalPolicy[] = [
  { key: 'reseller_terms', name: 'Reseller Terms' },
  { key: 'vendor_terms', name: 'Vendor Terms' },
  { key: 'kvkk_notice', name: 'KVKK Aydınlatma Metni', perUserAck: true },
  { key: 'commercial_consent', name: 'Ticari İleti (ETK) Onayı', perUserAck: true, isMarketing: true },
]

export type PolicyRowState = {
  key: string
  name: string
  agreement?: PolicyAgreement
  draft?: PolicyVersion
  published?: PolicyVersion
  status: 'none' | 'draft' | 'published'
  activeVersion?: PolicyVersion
  presentLocales: string[]
  missingLocales: string[]
  perUserAck?: boolean
  isMarketing?: boolean
}

// A policy can be deleted only when it was never live: an existing agreement
// with no published/retired version and no audience rules. The backend is the
// authoritative gate (also rejects acceptance attempts/acceptances).
function isDeletable(row: PolicyRowState): boolean {
  const a = row.agreement
  if (!a || row.published) return false
  if (!a.versions.every((v) => v.status === 'draft')) return false
  return (a.applicability?.length ?? 0) === 0
}

function deriveRow(
  key: string,
  name: string,
  agreement?: PolicyAgreement,
  canonical?: CanonicalPolicy,
): PolicyRowState {
  const versions = agreement?.versions ?? []
  const draft = versions.find((v) => v.status === 'draft')
  const published = versions
    .filter((v) => v.status === 'published')
    .sort((a, b) => b.version - a.version)[0]
  const activeVersion = draft ?? published
  const status: PolicyRowState['status'] = draft ? 'draft' : published ? 'published' : 'none'
  return {
    key,
    name: agreement?.name ?? name,
    agreement,
    draft,
    published,
    status,
    activeVersion,
    presentLocales: activeVersion?.present_locales ?? [],
    missingLocales: activeVersion?.missing_locales ?? [...SUPPORTED_AGREEMENT_LOCALES],
    perUserAck: agreement?.per_user_ack ?? canonical?.perUserAck,
    isMarketing: agreement?.is_marketing_consent ?? canonical?.isMarketing,
  }
}

export function PoliciesPage() {
  const t = useT(policiesI18n)
  const queryClient = useQueryClient()
  const [openKey, setOpenKey] = useState<string | null>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  useEscapeKey(menuOpen, () => setMenuOpen(false))
  const [deleteRow, setDeleteRow] = useState<PolicyRowState | null>(null)

  const listQ = useQuery({ queryKey: ['admin-policies'], queryFn: api.listPolicies })

  const rows = useMemo(() => {
    const policies = listQ.data ?? []
    const byKey = new Map(policies.map((p) => [p.key, p]))
    const canonical = CANONICAL_POLICIES.map((c) => deriveRow(c.key, c.name, byKey.get(c.key), c))
    const extra = policies
      .filter((p) => !CANONICAL_POLICIES.some((c) => c.key === p.key))
      .map((p) => deriveRow(p.key, p.name, p))
    return [...canonical, ...extra]
  }, [listQ.data])

  const openRow = rows.find((r) => r.key === openKey) ?? null

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['admin-policies'] })

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <header>
        <h1 className="flex items-center gap-2 text-xl font-semibold tracking-tight text-slate-900 appearance-dark:text-[var(--color-text)]">
          <ScrollText className="h-5 w-5 text-slate-500 appearance-dark:text-[var(--color-text-muted)]" />
          {t.title}
        </h1>
      </header>

      <section className="rounded-xl border border-slate-200 appearance-dark:border-[var(--color-border)] bg-white appearance-dark:bg-[var(--color-surface)] shadow-sm">
        <div className="flex items-start justify-between border-b border-slate-100 appearance-dark:border-[var(--color-border)] px-5 py-4">
          <div>
            <h2 className="text-sm font-semibold text-slate-900 appearance-dark:text-[var(--color-text)]">{t.writtenPolicies}</h2>
            <p className="mt-0.5 text-xs text-slate-500 appearance-dark:text-[var(--color-text-muted)]">{t.writtenPoliciesHint}</p>
          </div>
          <div className="relative">
            <button
              type="button"
              aria-label={t.moreActions}
              onClick={() => setMenuOpen((v) => !v)}
              className="rounded-md p-1.5 text-slate-500 appearance-dark:text-[var(--color-text-muted)] hover:bg-slate-100 appearance-dark:hover:bg-[var(--color-surface-muted)]"
              data-testid="policies-overflow"
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-10" aria-hidden="true" onClick={() => setMenuOpen(false)} />
                <div className="absolute right-0 z-20 mt-1 w-52 rounded-md border border-slate-200 appearance-dark:border-[var(--color-border)] bg-white appearance-dark:bg-[var(--color-surface)] py-1 shadow-lg">
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false)
                      setOpenKey('__new__')
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 appearance-dark:text-[var(--color-text)] hover:bg-slate-50 appearance-dark:hover:bg-[var(--color-surface-muted)]"
                    data-testid="add-policy"
                  >
                    <Plus className="h-4 w-4 text-slate-400 appearance-dark:text-[var(--color-text-muted)]" />
                    {t.addPolicy}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        <ul>
          {rows.map((row) => (
            <PolicyRow
              key={row.key}
              row={row}
              onOpen={() => setOpenKey(row.key)}
              onDelete={isDeletable(row) ? () => setDeleteRow(row) : undefined}
            />
          ))}
        </ul>
      </section>

      {deleteRow && deleteRow.agreement && (
        <DeleteDraftModal
          agreementId={deleteRow.agreement.id}
          policyName={deleteRow.name}
          onClose={() => setDeleteRow(null)}
          onDeleted={refresh}
        />
      )}

      {openRow && (
        <PolicyEditorModal
          key={openRow.key}
          row={openRow}
          onClose={() => setOpenKey(null)}
          onChanged={refresh}
        />
      )}
      {openKey === '__new__' && (
        <PolicyEditorModal
          key="__new__"
          row={{
            key: '',
            name: '',
            status: 'none',
            presentLocales: [],
            missingLocales: [...SUPPORTED_AGREEMENT_LOCALES],
          }}
          isNew
          onClose={() => setOpenKey(null)}
          onChanged={refresh}
        />
      )}
    </div>
  )
}

function statusMeta(
  status: PolicyRowState['status'],
  t: (typeof policiesI18n)['en'],
) {
  switch (status) {
    case 'published':
      return { label: t.statusPublished, cls: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20' }
    case 'draft':
      return { label: t.statusDraft, cls: 'bg-amber-50 text-amber-700 ring-amber-600/20' }
    default:
      return { label: t.statusNone, cls: 'bg-slate-100 appearance-dark:bg-[var(--color-surface-muted)] text-slate-500 appearance-dark:text-[var(--color-text-muted)] ring-slate-500/10' }
  }
}

function PolicyRow({
  row,
  onOpen,
  onDelete,
}: {
  row: PolicyRowState
  onOpen: () => void
  onDelete?: () => void
}) {
  const t = useT(policiesI18n)
  const [menuOpen, setMenuOpen] = useState(false)
  useEscapeKey(menuOpen, () => setMenuOpen(false))
  const meta = statusMeta(row.status, t)
  const completeness =
    row.status === 'none'
      ? null
      : SUPPORTED_AGREEMENT_LOCALES.map((loc) => {
          const has = row.presentLocales.includes(loc)
          return `${agreementLocaleShortLabel(loc)} ${has ? '✓' : '—'}`
        }).join(' · ')

  return (
    <li className="flex items-center border-b border-slate-100 appearance-dark:border-[var(--color-border)] pr-3 last:border-b-0 hover:bg-slate-50 appearance-dark:hover:bg-[var(--color-surface-muted)]">
      <button
        type="button"
        onClick={onOpen}
        className="flex min-w-0 flex-1 items-center gap-3 px-5 py-3.5 text-left"
        data-testid={`policy-row-${row.key}`}
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-slate-100 appearance-dark:bg-[var(--color-surface-muted)] text-slate-500 appearance-dark:text-[var(--color-text-muted)]">
          <FileText className="h-4 w-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium text-slate-800 appearance-dark:text-[var(--color-text)]">{row.name}</span>
          {row.status !== 'none' && (
            <span className="mt-0.5 block truncate text-xs text-slate-400 appearance-dark:text-[var(--color-text-muted)]">
              {t.version} {row.activeVersion?.version} · {completeness}
            </span>
          )}
        </span>
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${meta.cls}`}
        >
          {meta.label}
        </span>
      </button>
      {onDelete ? (
        <div className="relative shrink-0">
          <button
            type="button"
            aria-label={t.rowActions}
            onClick={() => setMenuOpen((v) => !v)}
            className="rounded-md p-1.5 text-slate-400 appearance-dark:text-[var(--color-text-muted)] hover:bg-slate-200 appearance-dark:hover:bg-[var(--color-surface-muted)] hover:text-slate-600 appearance-dark:hover:text-[var(--color-text-muted)]"
            data-testid={`policy-row-overflow-${row.key}`}
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-10" aria-hidden="true" onClick={() => setMenuOpen(false)} />
              <div className="absolute right-0 z-20 mt-1 w-52 rounded-md border border-slate-200 appearance-dark:border-[var(--color-border)] bg-white appearance-dark:bg-[var(--color-surface)] py-1 shadow-lg">
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false)
                    onDelete()
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-rose-600 hover:bg-rose-50"
                  data-testid={`delete-draft-${row.key}`}
                >
                  <Trash2 className="h-4 w-4" />
                  {t.deleteDraftPolicy}
                </button>
              </div>
            </>
          )}
        </div>
      ) : (
        <ChevronRight className="h-4 w-4 shrink-0 text-slate-300 appearance-dark:text-[var(--color-text-muted)]" />
      )}
    </li>
  )
}
